import React, { ReactNode, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface IMetaTags {
    title: string;
    description: string;
    children: ReactNode;
    thumbnailUrl?: string;
    keywords?: string;
    canonicalUrl?: string;
    structuredData?: object;
    ogType?: string;
    twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
}

const MetaTags: React.FC<IMetaTags> = ({
    title,
    description,
    thumbnailUrl,
    children,
    keywords,
    canonicalUrl,
    structuredData,
    ogType = 'website',
    twitterCard = 'summary_large_image',
}) => {
    const location = useLocation();
    const [thumbnailDimension, setThumbnailDimension] = useState({ width: '600', height: '400' });
    const baseUrl = 'https://thspian.com';
    const defaultImage = 'https://df41u66howm6r.cloudfront.net/base.png';
    const imageUrl = thumbnailUrl || defaultImage;
    const canonical = canonicalUrl || `${baseUrl}${location.pathname}`;

    useEffect(() => {
        if (thumbnailUrl) {
            const fetchImageDimensions = async () => {
                try {
                    const img = new Image();
                    img.src = thumbnailUrl;
                    img.onload = () => {
                        const newWidth = Math.floor(img.width * 0.5);
                        const newHeight = Math.floor(img.height * 0.5);
                        setThumbnailDimension({ width: String(newWidth), height: String(newHeight) });
                    };
                } catch (error) {
                    console.error('Error fetching image dimensions:', error);
                }
            };
            fetchImageDimensions();
        }
    }, [thumbnailUrl]);

    return (
        <div style={{ width: '100%' }}>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>{title}</title>
                <meta name="description" content={description} />
                {keywords && <meta name="keywords" content={keywords} />}
                <link rel="canonical" href={canonical} />

                {/* Open Graph Tags */}
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content={ogType} />
                <meta property="og:url" content={canonical} />
                <meta property="og:image" content={imageUrl} />
                <meta property="og:image:url" content={imageUrl} />
                <meta property="og:image:secure_url" content={imageUrl} />
                <meta property="og:image:width" content={thumbnailDimension.width} />
                <meta property="og:image:height" content={thumbnailDimension.height} />
                <meta property="og:site_name" content="Thspian" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content={twitterCard} />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={imageUrl} />

                {/* Structured Data (JSON-LD) */}
                {structuredData && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}
            </Helmet>
            {children}
        </div>
    );
};

MetaTags.defaultProps = {
    description: 'Create | Share | Unleash',
    title: 'Thspian',
};

export { MetaTags };
